import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { BedDouble, Bath, Maximize2, MapPin, ArrowLeft, Check } from "lucide-react";
import BookingCalendar from "../components/BookingCalendar";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function PropertyDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { format } = useCurrency();
  const [property, setProperty] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProperty(r.data)).catch(() => setProperty(null));
  }, [id]);

  if (!property) {
    return <div className="max-w-[1200px] mx-auto px-6 py-32 text-[#86868b]">—</div>;
  }

  const suffix =
    property.category === "rental_long" ? t.property.perMonth
    : property.category === "rental_short" ? t.property.perNight
    : "";

  const formatted = format(property.price);
  const heroImg = resolveImg(property.images?.[activeImg] || property.images?.[0]);
  const isShortRental = property.category === "rental_short";

  return (
    <main className="pt-10" data-testid="property-detail-page">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
        <Link to="/vente" className="inline-flex items-center gap-2 text-[13px] text-[#86868b] hover:text-[#1d1d1f] mb-6" data-testid="detail-back-link">
          <ArrowLeft size={14} /> {t.property.backTo}
        </Link>

        <div className="squircle relative aspect-[21/10] overflow-hidden bg-[#f5f5f7]">
          {heroImg && <img src={heroImg} alt={property.title} className="w-full h-full object-cover" />}
        </div>

        {property.images?.length > 1 && (
          <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar">
            {property.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`squircle flex-shrink-0 w-28 h-20 border-2 transition ${i === activeImg ? "border-[#1d1d1f]" : "border-transparent opacity-70 hover:opacity-100"}`}
                data-testid={`thumb-${i}`}
              >
                <img src={resolveImg(img)} alt="" className="w-full h-full object-cover rounded-[28px]" />
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-14">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 text-[13px] text-[#86868b] mb-3">
              <MapPin size={13} strokeWidth={1.5} />
              <span>{property.location}, {property.city}, {property.country}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-[#1d1d1f] mb-6">
              {property.title}
            </h1>
            <div className="flex flex-wrap gap-8 py-6 border-y border-black/5 mb-10">
              {property.bedrooms > 0 && <Stat icon={BedDouble} label={`${property.bedrooms} ${t.property.bedrooms}`} />}
              {property.bathrooms > 0 && <Stat icon={Bath} label={`${property.bathrooms} ${t.property.bathrooms}`} />}
              {property.surface > 0 && <Stat icon={Maximize2} label={`${property.surface} ${t.property.surface}`} />}
            </div>

            <h2 className="text-[12px] uppercase tracking-[0.2em] text-[#86868b] mb-3">{t.property.description}</h2>
            <p className="text-[16px] text-[#1d1d1f] leading-relaxed whitespace-pre-line mb-12">
              {property.description}
            </p>

            {property.amenities?.length > 0 && (
              <>
                <h2 className="text-[12px] uppercase tracking-[0.2em] text-[#86868b] mb-4">{t.property.amenities}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-12">
                  {property.amenities.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-[14px] text-[#1d1d1f]">
                      <Check size={14} strokeWidth={1.8} />
                      {a}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="lg:col-span-1">
            {isShortRental ? (
              <div className="sticky top-24">
                <div className="rounded-3xl bg-white border border-black/5 p-6 mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-[32px] font-semibold tracking-tight text-[#1d1d1f]">{formatted}</span>
                    <span className="text-[14px] text-[#86868b]">{suffix}</span>
                  </div>
                </div>
                <BookingCalendar property={property} />
              </div>
            ) : (
              <div className="sticky top-24 rounded-3xl bg-[#f5f5f7] p-8">
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-[36px] font-semibold tracking-tight text-[#1d1d1f]">{formatted}</span>
                  <span className="text-[14px] text-[#86868b]">{suffix}</span>
                </div>
                <button className="w-full btn-pill btn-primary text-[14px] mb-3" data-testid="detail-book-btn">
                  {t.property.contactAgency}
                </button>
                <button className="w-full btn-pill btn-secondary text-[14px]" data-testid="detail-contact-btn">
                  {lang === "fr" ? "Demander une visite" : "Request a visit"}
                </button>
              </div>
            )}
          </aside>
        </div>
      </div>
    </main>
  );
}

function Stat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[14px] text-[#1d1d1f]">
      <Icon size={16} strokeWidth={1.5} />
      {label}
    </div>
  );
}
